/*
 * Carbisdale - Layout-Modul
 *
 * Verwaltet die 4 Bildschirm-Bereiche (Panes):
 * - Outline (oben links, 28%)
 * - Main (oben mitte, 44%)
 * - Context (oben rechts, 28%)
 * - Core Console (unten, 30% Höhe)
 *
 * Jeder Bereich (außer Main) kann:
 * - Eingeklappt werden (COLLAPSED)
 * - Normal angezeigt werden (NORMAL)
 * - Vollbild angezeigt werden (FULLSCREEN)
 *
 * Navigation:
 * - Tab/Shift-Tab: Schneller Bereichswechsel (nur Cursor-Positionen)
 * - Ctrl-Tab/Ctrl-Shift-Tab: Layout-Navigation (Controls + Cursors)
 */

import { PaneState, PaneId, ConsoleFocus, LAYOUT } from './config.js'

/**
 * Einzelner Bereich auf dem Bildschirm.
 */
export class Pane {
    /**
     * @param {string} id - PaneId
     * @param {string} title - Anzeigename
     * @param {boolean} canCollapse - Kann eingeklappt werden?
     */
    constructor(id, title, canCollapse = true) {
        this.id = id
        this.title = title
        this.canCollapse = canCollapse
        this.state = PaneState.NORMAL
        this.previousState = null  // Für Rückkehr aus Fullscreen

        // Letzte Cursor-Position in diesem Bereich
        this.cursorPosition = { x: 0, y: 0 }

        // Berechnete Grenzen (in Zeichen-Koordinaten)
        // Werden von LayoutManager.calculateBounds() gesetzt
        this.bounds = {
            x: 0,       // Linke Spalte
            y: 0,       // Obere Zeile
            width: 0,   // Breite in Zeichen
            height: 0   // Höhe in Zeichen
        }

        // Innerer Bereich (ohne Rahmen) für Content
        this.innerBounds = {
            x: 0,
            y: 0,
            width: 0,
            height: 0
        }
    }

    /**
     * Prüft ob der Bereich sichtbar ist (nicht von anderem Fullscreen überdeckt).
     * Wird vom LayoutManager gesetzt.
     */
    isVisible() {
        return this.bounds.width > 0 && this.bounds.height > 0
    }

    /**
     * Prüft ob der Bereich ausgeklappt ist (NORMAL oder FULLSCREEN).
     */
    isExpanded() {
        return this.state === PaneState.NORMAL || this.state === PaneState.FULLSCREEN
    }
}

/**
 * Stations-Typ für Navigation
 */
export const StationType = Object.freeze({
    CONTROL_EXPAND: 'CONTROL_EXPAND',     // + Button
    CONTROL_COLLAPSE: 'CONTROL_COLLAPSE', // - Button
    CONTROL_SEPARATOR: 'CONTROL_SEPARATOR', // = Button (Separator einfügen, Top-Border)
    CONTROL_BACKGROUND: 'CONTROL_BACKGROUND', // = Button (Background einfügen, Bottom-Border)
    CONTROL_DICTATION: 'CONTROL_DICTATION', // * Button (Diktat, Bottom-Border links)
    CONTROL_ABORT: 'CONTROL_ABORT',       // ! Button (Agent-Abort, Bottom-Border links auf Core-Console)
    CONTROL_LOCK: 'CONTROL_LOCK',         // v Button (Section Lock, Main-Pane Top-Border)
    CURSOR: 'CURSOR'                      // Cursor-Position im Bereich
})

/**
 * Control-Typ für +/- Buttons (für Abwärtskompatibilität)
 */
export const ControlType = Object.freeze({
    EXPAND: 'EXPAND',     // + Button
    COLLAPSE: 'COLLAPSE', // - Button
    SEPARATOR: 'SEPARATOR', // = Button (Separator einfügen, Top-Border)
    BACKGROUND: 'BACKGROUND', // = Button (Background einfügen, Bottom-Border)
    DICTATION: 'DICTATION', // * Button (Diktat starten/stoppen, Bottom-Border links)
    ABORT: 'ABORT',        // ! Button (Agent-Abort, Bottom-Border links auf Core-Console)
    LOCK: 'LOCK',          // v Button (Section Lock, Main-Pane Top-Border)
    SYNC_WARNING: 'SYNC_WARNING' // ! Glyph (Cloud-Sync gestoert, Main-Pane Bottom-Border rechts)
})

/**
 * Eine Station in der Navigation (Control oder Cursor-Position)
 */
export class NavigationStation {
    /**
     * @param {string} paneId - Zugehöriger Bereich
     * @param {string} type - StationType
     * @param {number} x - Spalten-Position
     * @param {number} y - Zeilen-Position
     */
    constructor(paneId, type, x, y) {
        this.paneId = paneId
        this.type = type
        this.x = x
        this.y = y
    }

    isControl() {
        return this.type === StationType.CONTROL_EXPAND ||
               this.type === StationType.CONTROL_COLLAPSE ||
               this.type === StationType.CONTROL_SEPARATOR ||
               this.type === StationType.CONTROL_BACKGROUND ||
               this.type === StationType.CONTROL_DICTATION ||
               this.type === StationType.CONTROL_ABORT ||
               this.type === StationType.CONTROL_LOCK
    }

    isCursor() {
        return this.type === StationType.CURSOR
    }
}

/**
 * Ein Control (+/-) auf dem Bildschirm (Legacy, für Maus-Handling)
 */
export class Control {
    /**
     * @param {string} paneId - Zugehöriger Bereich
     * @param {string} type - ControlType (EXPAND/COLLAPSE)
     * @param {number} x - Spalten-Position
     * @param {number} y - Zeilen-Position
     */
    constructor(paneId, type, x, y) {
        this.paneId = paneId
        this.type = type
        this.x = x
        this.y = y
    }
}

/**
 * Verwaltet das Layout aller Bereiche.
 */
export class LayoutManager {
    /**
     * @param {number} cols - Anzahl Spalten
     * @param {number} rows - Anzahl Zeilen
     */
    constructor(cols, rows) {
        this.cols = cols
        this.rows = rows

        // Die 5 Bereiche erstellen. Memory startet als HIDDEN — er wird
        // erst sichtbar, wenn `MemoryEditor.open()` ihn auf FULLSCREEN setzt.
        this.panes = {
            [PaneId.OUTLINE]: new Pane(PaneId.OUTLINE, 'Outline', true),
            [PaneId.MAIN]: new Pane(PaneId.MAIN, 'Main', true),
            [PaneId.CONTEXT]: new Pane(PaneId.CONTEXT, 'Context', true),
            [PaneId.CORE]: new Pane(PaneId.CORE, 'Core', true),
            [PaneId.MEMORY]: new Pane(PaneId.MEMORY, 'AI Memory', false),
            [PaneId.PERSONAL_DICT]: new Pane(PaneId.PERSONAL_DICT, 'Personal Dictionary', false)
        }
        this.panes[PaneId.MEMORY].state = PaneState.HIDDEN
        this.panes[PaneId.PERSONAL_DICT].state = PaneState.HIDDEN

        // Aktiver Editor-Bereich (Outline/Main/Context — nie CORE)
        this._activeEditorPane = PaneId.MAIN

        // Console-Fokus (NONE/HOVER/FOCUSED)
        this.consoleFocus = ConsoleFocus.NONE

        // Aktuelle Station (Control oder Cursor-Position)
        // null = im Bereich (Cursor-Position des aktiven Bereichs)
        this.currentStation = null

        // Hover-State für Maus
        this.hoveredControl = null

        // Proportional-Modus (für 3-Zellen Hit-Testing bei Icon-Controls)
        this._isProportional = false

        // Cache für Navigations-Stationen
        this._allStations = []      // Alle Stationen (für Ctrl-Tab)
        this._cursorStations = []   // Nur Cursor-Positionen (für Tab)
        this._visibleControls = []  // Nur Controls (für Maus/Rendering)

        // Callback für Separator-Einfügung (wird von main.js gesetzt)
        this._onSeparatorInsert = null

        // Callback für Background-Einfügung (wird von main.js gesetzt)
        this._onBackgroundInsert = null

        // Callback für Diktat-Toggle (wird von main.js gesetzt)
        this._onDictationToggle = null

        // Callback für Agent-Abort (wird von main.js gesetzt)
        this._onAgentAbort = null
        this._onSyncWarningClick = null

        // Callback für Section-Lock-Toggle (wird von main.js gesetzt)
        this._onLockToggle = null

        // Callback nach jedem +/-Klick auf Pane-Controls — main.js nutzt das,
        // um Cursor-State (z.B. Separator-Edit-Pane) aufs neue Layout zu mappen,
        // bevor der nächste Render läuft.
        this._onPaneStateChanged = null

        // Flags: Diktat-Zustand (steuert Control-Farbe und Symbol)
        this._isDictationStarting = false  // Orange: wird gestartet
        this._isDictationActive = false    // Grün: läuft

        // Flag: AI-Agent arbeitet (zeigt (!) statt (*) auf der Core-Console)
        this._isAgentBusy = false

        // Flag: ob Background-Separator existiert (steuert Sichtbarkeit des Bottom-Controls)
        this._hasBackground = false
        this._syncWarning = false   // Cloud-Sync gestoert → (!) am Main-Bottom-Border

        // Flag: ob der aktuelle Abschnitt (am Cursor) gesperrt ist
        this._isCurrentSectionLocked = false

        // Tab-Leiste: sichtbar bei Mouse-Hover am oberen Rand
        this.tabBarVisible = false

        // Dirty-Flags für Re-Rendering
        this.isDirty = true
        this.borderDirty = true  // Pane-Rahmen müssen neu gezeichnet werden

        // Initiale Berechnung
        this.calculateBounds()
    }

    /**
     * Computed Property: aktiver Bereich.
     * Gibt CORE zurück wenn Console FOCUSED, sonst den aktiven Editor-Pane.
     * @type {string}
     */
    get activePane() {
        return this.consoleFocus === ConsoleFocus.FOCUSED
            ? PaneId.CORE : this._activeEditorPane
    }

    set activePane(paneId) {
        if (paneId === PaneId.CORE) {
            this.consoleFocus = ConsoleFocus.FOCUSED
        } else {
            this._activeEditorPane = paneId
            this.consoleFocus = ConsoleFocus.NONE
        }
    }

    /**
     * Gibt den aktiven Bereich zurück.
     * @returns {Pane}
     */
    getActivePane() {
        return this.panes[this.activePane]
    }

    /**
     * Gibt immer den aktiven Editor-Bereich zurück (nie CORE).
     * @returns {string}
     */
    getActiveEditorPane() {
        return this._activeEditorPane
    }

    /**
     * Setzt den aktiven Editor-Bereich. Console-Fokus wird aufgehoben.
     * @param {string} paneId
     */
    setActivePane(paneId) {
        if (!this.panes[paneId] || !this.panes[paneId].isVisible()) return
        if (paneId === PaneId.CORE) {
            this.consoleFocus = ConsoleFocus.FOCUSED
        } else {
            this._activeEditorPane = paneId
            this.consoleFocus = ConsoleFocus.NONE
            // currentStation auf eine Cursor-Station des neuen Panes umlenken,
            // wenn sie noch auf einem anderen Pane steht. Sonst zeichnet
            // `renderCursorLayer` den Cursor nicht (`onEditorStation` schlägt
            // fehl, weil currentStation.paneId !== editorPane.id).
            if (this.currentStation && this.currentStation.paneId !== paneId) {
                this.currentStation = this._cursorStations.find(
                    s => s.paneId === paneId
                ) || null
            }
        }
        this.isDirty = true
        this.borderDirty = true
        this._updateVisibleControls()
    }

    /**
     * Setzt den Console-Fokus-Zustand.
     * @param {string} state - ConsoleFocus.NONE/HOVER/FOCUSED
     */
    setConsoleFocus(state) {
        if (this.consoleFocus === state) return
        this.consoleFocus = state
        this.isDirty = true
        this.borderDirty = true
        this._updateVisibleControls()
    }

    /** Console im FOCUSED-Zustand? */
    isConsoleFocused() {
        return this.consoleFocus === ConsoleFocus.FOCUSED
    }

    /** Console im HOVER- oder FOCUSED-Zustand? (Cursor blinkt, Tastatur an Console) */
    isConsoleActive() {
        return this.consoleFocus !== ConsoleFocus.NONE
    }

    /**
     * Berechnet die Bounds aller Bereiche basierend auf aktuellem Zustand.
     * Muss nach Resize oder State-Änderungen aufgerufen werden.
     */
    calculateBounds() {
        // Prüfen ob ein Bereich im Fullscreen ist
        const fullscreenPane = this._getFullscreenPane()

        if (fullscreenPane) {
            this._calculateFullscreenBounds(fullscreenPane)
        } else {
            this._calculateNormalBounds()
        }

        // Controls neu berechnen
        this._updateVisibleControls()
        this.isDirty = true
        this.borderDirty = true
    }

    /**
     * Findet einen Bereich im Fullscreen-Modus.
     * Memory und Personal Dict sind transient-modal — sie haben Vorrang vor
     * einem ggf. parallel FULLSCREEN-gesetzten Main-Pane. Sonst waere ein
     * Memory-Open ueber einer Main-Maximierung unsichtbar (Iteration faende
     * MAIN zuerst und blendete MEMORY weg).
     * @returns {Pane|null}
     */
    _getFullscreenPane() {
        const memory = this.panes[PaneId.MEMORY]
        if (memory && memory.state === PaneState.FULLSCREEN) return memory
        const dict = this.panes[PaneId.PERSONAL_DICT]
        if (dict && dict.state === PaneState.FULLSCREEN) return dict

        for (const pane of Object.values(this.panes)) {
            if (pane.state === PaneState.FULLSCREEN) {
                return pane
            }
        }
        return null
    }

    /**
     * Berechnet Bounds wenn ein Bereich im Fullscreen ist.
     * Alle anderen Bereiche werden auf 0 gesetzt (unsichtbar).
     * @param {Pane} fullscreenPane
     */
    _calculateFullscreenBounds(fullscreenPane) {
        // Alle Bereiche unsichtbar machen
        for (const pane of Object.values(this.panes)) {
            pane.bounds = { x: 0, y: 0, width: 0, height: 0 }
            pane.innerBounds = { x: 0, y: 0, width: 0, height: 0 }
        }

        // Tab-Leiste nimmt eine Zeile weg wenn sichtbar
        const topY = this.tabBarVisible ? 1 : 0

        // Fullscreen-Bereich bekommt alles (unterhalb der Tab-Leiste)
        fullscreenPane.bounds = {
            x: 0,
            y: topY,
            width: this.cols,
            height: this.rows - topY
        }
        fullscreenPane.innerBounds = {
            x: 1,
            y: topY + 1,
            width: this.cols - 2,
            height: this.rows - topY - 2
        }
    }

    /**
     * Berechnet Bounds im normalen Modus (kein Fullscreen).
     */
    _calculateNormalBounds() {
        const outline = this.panes[PaneId.OUTLINE]
        const main = this.panes[PaneId.MAIN]
        const context = this.panes[PaneId.CONTEXT]
        const core = this.panes[PaneId.CORE]

        // Memory-Pane und Personal-Dict-Pane sind im Normal-Layout immer
        // unsichtbar. Bounds explizit nullen, damit ein vorheriger FULLSCREEN-
        // Zustand kein Layout-Echo hinterlaesst.
        const memoryPane = this.panes[PaneId.MEMORY]
        if (memoryPane) {
            memoryPane.bounds = { x: 0, y: 0, width: 0, height: 0 }
            memoryPane.innerBounds = { x: 0, y: 0, width: 0, height: 0 }
        }
        const dictPane = this.panes[PaneId.PERSONAL_DICT]
        if (dictPane) {
            dictPane.bounds = { x: 0, y: 0, width: 0, height: 0 }
            dictPane.innerBounds = { x: 0, y: 0, width: 0, height: 0 }
        }

        // Höhe der Core Console (unten)
        const coreCollapsed = core.state === PaneState.COLLAPSED
        const coreHeight = coreCollapsed ? 1 : Math.floor(this.rows * LAYOUT.CORE_HEIGHT)

        // Tab-Leiste nimmt eine Zeile weg wenn sichtbar
        const topY = this.tabBarVisible ? 1 : 0

        // Höhe der oberen Bereiche
        const topHeight = this.rows - coreHeight - topY

        // Collapse-Status der oberen drei Panes
        const outlineCollapsed = outline.state === PaneState.COLLAPSED
        const mainCollapsed = main.state === PaneState.COLLAPSED
        const contextCollapsed = context.state === PaneState.COLLAPSED

        // Collapsed-Bereiche haben Breite 1 (nur die Linie mit +)
        let outlineWidth, mainWidth, contextWidth

        // Zähle offene obere Panes
        const openCount = [outlineCollapsed, mainCollapsed, contextCollapsed].filter(c => !c).length

        if (openCount === 1) {
            // Nur ein Pane offen: bekommt alles (minus 2 für die collapsed-Linien)
            if (!outlineCollapsed) {
                outlineWidth = this.cols - 2
                mainWidth = 1
                contextWidth = 1
            } else if (!mainCollapsed) {
                outlineWidth = 1
                mainWidth = this.cols - 2
                contextWidth = 1
            } else {
                outlineWidth = 1
                mainWidth = 1
                contextWidth = this.cols - 2
            }
        } else if (openCount === 2) {
            // Zwei Panes offen
            if (mainCollapsed) {
                // Main collapsed: Outline und Context je 50%
                mainWidth = 1
                const remainingWidth = this.cols - 1
                outlineWidth = Math.floor(remainingWidth / 2)
                contextWidth = remainingWidth - outlineWidth
            } else if (outlineCollapsed) {
                // Outline collapsed: Main und Context teilen den Rest
                outlineWidth = 1
                contextWidth = Math.floor((this.cols - 1) * LAYOUT.CONTEXT_WIDTH / (LAYOUT.CONTEXT_WIDTH + LAYOUT.MAIN_WIDTH))
                mainWidth = this.cols - 1 - contextWidth
            } else {
                // Context collapsed: Outline und Main teilen den Rest
                contextWidth = 1
                outlineWidth = Math.floor((this.cols - 1) * LAYOUT.OUTLINE_WIDTH / (LAYOUT.OUTLINE_WIDTH + LAYOUT.MAIN_WIDTH))
                mainWidth = this.cols - 1 - outlineWidth
            }
        } else {
            // Alle drei offen: normale Proportionen
            outlineWidth = Math.floor(this.cols * LAYOUT.OUTLINE_WIDTH)
            contextWidth = Math.floor(this.cols * LAYOUT.CONTEXT_WIDTH)
            mainWidth = this.cols - outlineWidth - contextWidth
        }

        // Outline Bounds
        outline.bounds = {
            x: 0,
            y: topY,
            width: outlineWidth,
            height: topHeight
        }
        if (outlineCollapsed) {
            outline.innerBounds = { x: 0, y: 0, width: 0, height: 0 }
        } else {
            outline.innerBounds = {
                x: 1,
                y: topY + 1,
                width: outlineWidth - 2,
                height: topHeight - 2
            }
        }

        // Main Bounds
        main.bounds = {
            x: outlineWidth,
            y: topY,
            width: mainWidth,
            height: topHeight
        }
        if (mainCollapsed) {
            main.innerBounds = { x: 0, y: 0, width: 0, height: 0 }
        } else {
            main.innerBounds = {
                x: outlineWidth + 1,
                y: topY + 1,
                width: mainWidth - 2,
                height: topHeight - 2
            }
        }

        // Context Bounds
        context.bounds = {
            x: outlineWidth + mainWidth,
            y: topY,
            width: contextWidth,
            height: topHeight
        }
        if (contextCollapsed) {
            context.innerBounds = { x: 0, y: 0, width: 0, height: 0 }
        } else {
            context.innerBounds = {
                x: outlineWidth + mainWidth + 1,
                y: topY + 1,
                width: contextWidth - 2,
                height: topHeight - 2
            }
        }

        // Core Bounds (topY + topHeight = Startzeile der Core Console)
        const coreY = topY + topHeight
        core.bounds = {
            x: 0,
            y: coreY,
            width: this.cols,
            height: coreHeight
        }
        if (coreCollapsed) {
            core.innerBounds = { x: 0, y: 0, width: 0, height: 0 }
        } else {
            core.innerBounds = {
                x: 1,
                y: coreY + 1,
                width: this.cols - 2,
                height: coreHeight - 2
            }
        }
    }

    /**
     * Zählt wie viele der oberen drei Panes (Outline, Main, Context) offen sind.
     * @returns {number}
     */
    _countOpenTopPanes() {
        const topPanes = [PaneId.OUTLINE, PaneId.MAIN, PaneId.CONTEXT]
        return topPanes.filter(id => this.panes[id].state !== PaneState.COLLAPSED).length
    }

    /**
     * Setzt Callback für Separator-Einfügung (von außen).
     */
    setOnSeparatorInsert(callback) {
        this._onSeparatorInsert = callback
    }

    /**
     * Setzt Callback für Background-Einfügung (von außen).
     */
    setOnBackgroundInsert(callback) {
        this._onBackgroundInsert = callback
    }

    /**
     * Setzt Callback für Diktat-Toggle (von außen).
     */
    setOnDictationToggle(callback) {
        this._onDictationToggle = callback
    }

    /**
     * Setzt Callback für Agent-Abort (von außen). Wird von (!) am Bottom-Border
     * der Core-Console getriggert, solange `isAgentBusy` true ist.
     */
    setOnAgentAbort(callback) {
        this._onAgentAbort = callback
    }

    /**
     * Setzt Callback für Section-Lock-Toggle (von außen).
     */
    setOnLockToggle(callback) {
        this._onLockToggle = callback
    }

    /**
     * Setzt Callback für Pane-State-Änderungen (von außen). Wird nach jedem
     * +/- Klick aufgerufen, damit Konsumenten Cursor-State ans neue Layout
     * mappen können.
     */
    setOnPaneStateChanged(callback) {
        this._onPaneStateChanged = callback
    }

    /**
     * Setzt/liest ob ein Background-Separator existiert.
     * Bei Änderung werden Controls aktualisiert.
     */
    setHasBackground(value) {
        if (this._hasBackground !== value) {
            this._hasBackground = value
            this.borderDirty = true
            this._updateVisibleControls()
        }
    }

    /**
     * Setzt/liest ob der Cloud-Sync des aktiven Projekts gestoert ist
     * (offline oder Pushes schlagen fehl). Zeigt das (!)-Warn-Control am
     * Bottom-Border des Main-Panes (Testphasen-Entscheidung 2026-07-02).
     */
    setSyncWarning(value) {
        if (this._syncWarning !== value) {
            this._syncWarning = value
            this.borderDirty = true
            this.isDirty = true
            this._updateVisibleControls()
        }
    }

    get hasSyncWarning() {
        return this._syncWarning
    }

    /**
     * Setzt Callback für Klick auf das Sync-Warn-Control (von außen) —
     * main.js zeigt darauf einen erklaerenden Dialog.
     */
    setOnSyncWarningClick(callback) {
        this._onSyncWarningClick = callback
    }

    get hasBackground() {
        return this._hasBackground
    }

    get isDictationStarting() {
        return this._isDictationStarting
    }

    set isDictationStarting(value) {
        if (this._isDictationStarting === value) return
        this._isDictationStarting = value
        this.borderDirty = true
        this.isDirty = true
    }

    get isDictationActive() {
        return this._isDictationActive
    }

    set isDictationActive(value) {
        if (this._isDictationActive === value) return
        this._isDictationActive = value
        this.borderDirty = true
        this.isDirty = true
    }

    get isAgentBusy() {
        return this._isAgentBusy
    }

    set isAgentBusy(value) {
        if (this._isAgentBusy === value) return
        this._isAgentBusy = value
        this.borderDirty = true
        this.isDirty = true
        // Sichtbare Controls neu berechnen — (!) ersetzt (*) auf Core-Pane
        this._updateVisibleControls()
    }

    get isCurrentSectionLocked() {
        return this._isCurrentSectionLocked
    }

    set isCurrentSectionLocked(value) {
        if (this._isCurrentSectionLocked === value) return
        this._isCurrentSectionLocked = value
        this.borderDirty = true
        this.isDirty = true
    }

    /**
     * Aktualisiert alle Navigations-Listen.
     */
    _updateVisibleControls() {
        this._visibleControls = []
        this._allStations = []
        this._cursorStations = []

        const fullscreenPane = this._getFullscreenPane()
        const paneOrder = [PaneId.OUTLINE, PaneId.MAIN, PaneId.CONTEXT, PaneId.CORE]
        const topPanes = [PaneId.OUTLINE, PaneId.MAIN, PaneId.CONTEXT]

        // Im Fullscreen-Modus: nur der Fullscreen-Bereich
        if (fullscreenPane) {
            // Kein + im Fullscreen! Nur - zum Verlassen
            const minusX = this._getControlPosition(fullscreenPane, ControlType.COLLAPSE, true)
            this._visibleControls.push(
                new Control(fullscreenPane.id, ControlType.COLLAPSE, minusX, 0)
            )
            this._allStations.push(
                new NavigationStation(fullscreenPane.id, StationType.CONTROL_COLLAPSE, minusX, 0)
            )

            // Bottom-Border links: (!) wenn Agent auf Core-Console arbeitet,
            // sonst (o) Diktat-Control wie gewohnt
            const fsBottomY = fullscreenPane.bounds.y + fullscreenPane.bounds.height - 1
            const fsCtrlX = fullscreenPane.bounds.x + 3
            const fsShowAbort = fullscreenPane.id === PaneId.CORE && this._isAgentBusy
            if (fsShowAbort) {
                this._visibleControls.push(
                    new Control(fullscreenPane.id, ControlType.ABORT, fsCtrlX, fsBottomY)
                )
                this._allStations.push(
                    new NavigationStation(fullscreenPane.id, StationType.CONTROL_ABORT, fsCtrlX, fsBottomY)
                )
            } else {
                this._visibleControls.push(
                    new Control(fullscreenPane.id, ControlType.DICTATION, fsCtrlX, fsBottomY)
                )
                this._allStations.push(
                    new NavigationStation(fullscreenPane.id, StationType.CONTROL_DICTATION, fsCtrlX, fsBottomY)
                )
            }

            // (v)/( ) Lock-Control für Main im Fullscreen — gleiche Position
            // wie im Normal-Modus (W-5, aligned mit Separator-Lock).
            if (fullscreenPane.id === PaneId.MAIN) {
                const fsLockX = fullscreenPane.bounds.x + fullscreenPane.bounds.width - 5
                if (fsLockX > fullscreenPane.bounds.x + 8) {
                    this._visibleControls.push(
                        new Control(fullscreenPane.id, ControlType.LOCK, fsLockX, fullscreenPane.bounds.y)
                    )
                    this._allStations.push(
                        new NavigationStation(fullscreenPane.id, StationType.CONTROL_LOCK, fsLockX, fullscreenPane.bounds.y)
                    )
                }
            }

            // Cursor-Position des Fullscreen-Bereichs
            const cursorX = fullscreenPane.innerBounds.x + fullscreenPane.cursorPosition.x
            const cursorY = fullscreenPane.innerBounds.y + fullscreenPane.cursorPosition.y
            this._allStations.push(
                new NavigationStation(fullscreenPane.id, StationType.CURSOR, cursorX, cursorY)
            )
            this._cursorStations.push(
                new NavigationStation(fullscreenPane.id, StationType.CURSOR, cursorX, cursorY)
            )
            return
        }

        // Zähle offene obere Panes (für Collapse-Control-Logik)
        const openTopPanes = this._countOpenTopPanes()

        // Normale Ansicht: Controls und Cursors in Reihenfolge
        for (const paneId of paneOrder) {
            const pane = this.panes[paneId]
            const isTopPane = topPanes.includes(paneId)

            if (pane.state === PaneState.COLLAPSED) {
                // Collapsed: nur + Control
                const plusX = this._getControlPosition(pane, ControlType.EXPAND, false)
                if (plusX !== null) {
                    this._visibleControls.push(
                        new Control(paneId, ControlType.EXPAND, plusX, pane.bounds.y)
                    )
                    this._allStations.push(
                        new NavigationStation(paneId, StationType.CONTROL_EXPAND, plusX, pane.bounds.y)
                    )
                }
            } else {
                // Expanded (NORMAL): + und ggf. - Control, dann Cursor
                const plusX = this._getControlPosition(pane, ControlType.EXPAND, false)
                if (plusX !== null) {
                    this._visibleControls.push(
                        new Control(paneId, ControlType.EXPAND, plusX, pane.bounds.y)
                    )
                    this._allStations.push(
                        new NavigationStation(paneId, StationType.CONTROL_EXPAND, plusX, pane.bounds.y)
                    )
                }

                // - Control nur wenn:
                // - canCollapse ist true UND
                // - bei oberen Panes: mindestens 2 müssen offen sein (sonst macht - keinen Sinn)
                // - bei Core: immer erlaubt
                const canShowCollapse = pane.canCollapse && (!isTopPane || openTopPanes > 1)
                if (canShowCollapse) {
                    const minusX = this._getControlPosition(pane, ControlType.COLLAPSE, false)
                    if (minusX !== null) {
                        this._visibleControls.push(
                            new Control(paneId, ControlType.COLLAPSE, minusX, pane.bounds.y)
                        )
                        this._allStations.push(
                            new NavigationStation(paneId, StationType.CONTROL_COLLAPSE, minusX, pane.bounds.y)
                        )
                    }
                }

                // (=) Control für Outline-Pane (Separator einfügen, Top-Border)
                if (paneId === PaneId.OUTLINE && pane.isExpanded()) {
                    // Position: rechte Seite der Titelzeile, 4 Zeichen vor rechter Ecke
                    const sepX = pane.bounds.x + pane.bounds.width - 4
                    if (sepX > pane.bounds.x + 8) {  // Mindestabstand zu linken Controls
                        this._visibleControls.push(
                            new Control(paneId, ControlType.SEPARATOR, sepX, pane.bounds.y)
                        )
                        this._allStations.push(
                            new NavigationStation(paneId, StationType.CONTROL_SEPARATOR, sepX, pane.bounds.y)
                        )
                    }

                    // (=) Control am Bottom-Border (Background einfügen)
                    // Nur sichtbar wenn kein Background-Separator existiert
                    if (!this._hasBackground) {
                        const bottomY = pane.bounds.y + pane.bounds.height - 1
                        const bgX = pane.bounds.x + pane.bounds.width - 4
                        if (bgX > pane.bounds.x + 8) {
                            this._visibleControls.push(
                                new Control(paneId, ControlType.BACKGROUND, bgX, bottomY)
                            )
                            this._allStations.push(
                                new NavigationStation(paneId, StationType.CONTROL_BACKGROUND, bgX, bottomY)
                            )
                        }
                    }
                }

                // (v)/( ) Lock-Control für Main-Pane (Section Lock, Top-Border)
                // Position width-5: aligned mit dem Lock-Control auf den Separator-
                // Linien (innerBounds.x + this.width - 4 + 1 = bounds.x + W - 5).
                if (paneId === PaneId.MAIN && pane.isExpanded()) {
                    const lockX = pane.bounds.x + pane.bounds.width - 5
                    if (lockX > pane.bounds.x + 8) {
                        this._visibleControls.push(
                            new Control(paneId, ControlType.LOCK, lockX, pane.bounds.y)
                        )
                        this._allStations.push(
                            new NavigationStation(paneId, StationType.CONTROL_LOCK, lockX, pane.bounds.y)
                        )
                    }
                }

                // Bottom-Border links (Control-Position: bounds.x + 3)
                // Reihenfolge wichtig — (!) und (*) teilen sich die Position:
                //   - (!) Agent-Abort: fix auf Core-Pane solange Agent arbeitet,
                //     unabhaengig vom activePane. User kann parallel diktieren.
                //   - (*) Diktat: wandert mit activePane. Auf Core unterdrueckt
                //     wenn (!) dort sitzt.
                const bottomY = pane.bounds.y + pane.bounds.height - 1
                const bottomCtrlX = pane.bounds.x + 3
                const showAbortHere = pane.isExpanded() && paneId === PaneId.CORE && this._isAgentBusy
                if (showAbortHere) {
                    this._visibleControls.push(
                        new Control(paneId, ControlType.ABORT, bottomCtrlX, bottomY)
                    )
                    this._allStations.push(
                        new NavigationStation(paneId, StationType.CONTROL_ABORT, bottomCtrlX, bottomY)
                    )
                }
                // (o) Dictation-Control auf dem aktiven Pane — nicht auf Core,
                // wenn dort (!) sitzt
                if (pane.isExpanded() && paneId === this.activePane && !showAbortHere) {
                    this._visibleControls.push(
                        new Control(paneId, ControlType.DICTATION, bottomCtrlX, bottomY)
                    )
                    this._allStations.push(
                        new NavigationStation(paneId, StationType.CONTROL_DICTATION, bottomCtrlX, bottomY)
                    )
                }

                // (!) Sync-Warn-Control am Main-Bottom-Border rechts — nur
                // solange der Cloud-Sync gestoert ist (setSyncWarning).
                // Display + Klick (Erklaer-Dialog), bewusst KEINE
                // NavigationStation: Tab-Navigation bleibt unveraendert.
                if (paneId === PaneId.MAIN && pane.isExpanded() && this._syncWarning) {
                    const warnX = pane.bounds.x + pane.bounds.width - 5
                    if (warnX > pane.bounds.x + 8) {
                        this._visibleControls.push(
                            new Control(paneId, ControlType.SYNC_WARNING, warnX, bottomY)
                        )
                    }
                }

                // Cursor-Position
                const cursorX = pane.innerBounds.x + pane.cursorPosition.x
                const cursorY = pane.innerBounds.y + pane.cursorPosition.y
                this._allStations.push(
                    new NavigationStation(paneId, StationType.CURSOR, cursorX, cursorY)
                )
                this._cursorStations.push(
                    new NavigationStation(paneId, StationType.CURSOR, cursorX, cursorY)
                )
            }
        }
    }

    /**
     * Berechnet die X-Position eines Controls.
     * @param {Pane} pane
     * @param {string} type - ControlType
     * @param {boolean} isFullscreen - Ist der Bereich im Fullscreen?
     * @returns {number|null}
     */
    _getControlPosition(pane, type, isFullscreen = false) {
        if (!pane.isVisible() && pane.state !== PaneState.COLLAPSED) {
            return null
        }

        // Für collapsed obere Bereiche: + ist direkt an der Pane-Position
        if (pane.state === PaneState.COLLAPSED) {
            if (pane.id === PaneId.OUTLINE || pane.id === PaneId.MAIN || pane.id === PaneId.CONTEXT) {
                return pane.bounds.x
            } else if (pane.id === PaneId.CORE) {
                return 3  // Position des + in "└─(+)─"
            }
        }

        // Fullscreen: nur - bei Position 3 (Format: "┌ (-) Title")
        if (isFullscreen) {
            return pane.bounds.x + 3  // Position des - in (-)
        }

        // Für expanded Bereiche: + und - in der Titelzeile
        // Neues Format: "┌─(+)─Title─" oder "┌─(+|-)─Title─"
        // Positionen:      3              3  5
        if (type === ControlType.EXPAND) {
            return pane.bounds.x + 3  // Position des + in (+)
        } else {
            return pane.bounds.x + 5  // Position des - in (+|-)
        }
    }

    /**
     * Gibt alle sichtbaren Controls zurück (für Rendering und Maus).
     * @returns {Control[]}
     */
    getVisibleControls() {
        return this._visibleControls
    }

    /**
     * Gibt die aktuelle Station zurück.
     * @returns {NavigationStation|null}
     */
    getCurrentStation() {
        return this.currentStation
    }

    /**
     * Prüft ob wir auf einem Control sind (nicht auf Cursor-Position).
     * @returns {boolean}
     */
    isOnControl() {
        return this.currentStation !== null && this.currentStation.isControl()
    }

    /**
     * Navigiert zum nächsten Bereich (Tab).
     * Springt nur durch Cursor-Positionen der expanded Bereiche.
     */
    navigateToNextPane() {
        // Core aus dem Tab-Zyklus ausschliessen
        const stations = this._cursorStations.filter(s => s.paneId !== PaneId.CORE)
        if (stations.length === 0) return

        // Finde aktuelle Position
        let currentIdx = stations.findIndex(s => s.paneId === this._activeEditorPane)
        if (currentIdx < 0) currentIdx = 0

        const nextIdx = (currentIdx + 1) % stations.length
        const nextStation = stations[nextIdx]

        this.currentStation = nextStation
        this._activeEditorPane = nextStation.paneId
        this.consoleFocus = ConsoleFocus.NONE
        this.isDirty = true
        this.borderDirty = true
    }

    /**
     * Navigiert zum vorherigen Bereich (Shift-Tab).
     */
    navigateToPreviousPane() {
        // Core aus dem Tab-Zyklus ausschliessen
        const stations = this._cursorStations.filter(s => s.paneId !== PaneId.CORE)
        if (stations.length === 0) return

        let currentIdx = stations.findIndex(s => s.paneId === this._activeEditorPane)
        if (currentIdx < 0) currentIdx = 0

        const prevIdx = (currentIdx - 1 + stations.length) % stations.length
        const prevStation = stations[prevIdx]

        this.currentStation = prevStation
        this._activeEditorPane = prevStation.paneId
        this.consoleFocus = ConsoleFocus.NONE
        this.isDirty = true
        this.borderDirty = true
    }

    /**
     * Navigiert zur nächsten Station (Ctrl-Tab).
     * Durchläuft Controls UND Cursor-Positionen.
     */
    navigateToNextStation() {
        if (this._allStations.length === 0) return

        let currentIdx = this._findCurrentStationIndex()

        // Zum nächsten
        const nextIdx = (currentIdx + 1) % this._allStations.length
        this.currentStation = this._allStations[nextIdx]

        // Wenn Cursor-Station: Bereich aktivieren
        if (this.currentStation.isCursor()) {
            this.activePane = this.currentStation.paneId
        }

        this.isDirty = true
        this.borderDirty = true
    }

    /**
     * Navigiert zur vorherigen Station (Ctrl-Shift-Tab).
     */
    navigateToPreviousStation() {
        if (this._allStations.length === 0) return

        let currentIdx = this._findCurrentStationIndex()

        // Zum vorherigen
        const prevIdx = (currentIdx - 1 + this._allStations.length) % this._allStations.length
        this.currentStation = this._allStations[prevIdx]

        // Wenn Cursor-Station: Bereich aktivieren
        if (this.currentStation.isCursor()) {
            this.activePane = this.currentStation.paneId
        }

        this.isDirty = true
        this.borderDirty = true
    }

    /**
     * Navigiert zum nächsten Control (Tab im Layout-Modus).
     * Überspringt Cursor-Stationen, bleibt nur bei Controls.
     */
    navigateToNextControl() {
        const controls = this._allStations.filter(s => s.isControl())
        if (controls.length === 0) return

        // Aktuellen Control-Index finden
        let currentIdx = -1
        if (this.currentStation && this.currentStation.isControl()) {
            currentIdx = controls.findIndex(
                s => s.paneId === this.currentStation.paneId && s.type === this.currentStation.type
            )
        }

        // Zum nächsten Control
        const nextIdx = (currentIdx + 1) % controls.length
        this.currentStation = controls[nextIdx]
        this.isDirty = true
        this.borderDirty = true
    }

    /**
     * Navigiert zum vorherigen Control (Shift-Tab im Layout-Modus).
     * Überspringt Cursor-Stationen, bleibt nur bei Controls.
     */
    navigateToPreviousControl() {
        const controls = this._allStations.filter(s => s.isControl())
        if (controls.length === 0) return

        // Aktuellen Control-Index finden
        let currentIdx = -1
        if (this.currentStation && this.currentStation.isControl()) {
            currentIdx = controls.findIndex(
                s => s.paneId === this.currentStation.paneId && s.type === this.currentStation.type
            )
        }

        // Zum vorherigen Control
        const prevIdx = (currentIdx - 1 + controls.length) % controls.length
        this.currentStation = controls[prevIdx]
        this.isDirty = true
        this.borderDirty = true
    }

    /**
     * Findet den Index der aktuellen Position in _allStations.
     * @returns {number}
     */
    _findCurrentStationIndex() {
        if (this.currentStation) {
            // Exakte Suche
            const idx = this._allStations.findIndex(
                s => s.paneId === this.currentStation.paneId &&
                     s.type === this.currentStation.type
            )
            if (idx !== -1) return idx
        }

        // Fallback: Cursor des aktiven Bereichs
        const idx = this._allStations.findIndex(
            s => s.paneId === this.activePane && s.type === StationType.CURSOR
        )
        return idx !== -1 ? idx : 0
    }

    /**
     * Prüft ob ein Pane ein Collapse-Control hat.
     * Bei den oberen drei Panes nur wenn mindestens 2 offen sind.
     * @param {string} paneId
     * @returns {boolean}
     */
    hasCollapseControl(paneId) {
        return this._visibleControls.some(
            c => c.paneId === paneId && c.type === ControlType.COLLAPSE
        )
    }

    /**
     * Prüft ob ein Separator-Control für einen Bereich sichtbar ist.
     * @param {string} paneId
     * @returns {boolean}
     */
    hasSeparatorControl(paneId) {
        return this._visibleControls.some(
            c => c.paneId === paneId && c.type === ControlType.SEPARATOR
        )
    }

    /**
     * Gibt das Separator-Control für einen Bereich zurück.
     * @param {string} paneId
     * @returns {Control|null}
     */
    getSeparatorControl(paneId) {
        return this._visibleControls.find(
            c => c.paneId === paneId && c.type === ControlType.SEPARATOR
        ) || null
    }

    /**
     * Prüft ob ein Background-Control für einen Bereich sichtbar ist.
     * @param {string} paneId
     * @returns {boolean}
     */
    hasBackgroundControl(paneId) {
        return this._visibleControls.some(
            c => c.paneId === paneId && c.type === ControlType.BACKGROUND
        )
    }

    /**
     * Gibt das Background-Control für einen Bereich zurück.
     * @param {string} paneId
     * @returns {Control|null}
     */
    getBackgroundControl(paneId) {
        return this._visibleControls.find(
            c => c.paneId === paneId && c.type === ControlType.BACKGROUND
        ) || null
    }

    /**
     * Prüft ob ein Dictation-Control für einen Bereich sichtbar ist.
     * @param {string} paneId
     * @returns {boolean}
     */
    hasDictationControl(paneId) {
        return this._visibleControls.some(
            c => c.paneId === paneId && c.type === ControlType.DICTATION
        )
    }

    /**
     * Gibt das Dictation-Control für einen Bereich zurück.
     * @param {string} paneId
     * @returns {Control|null}
     */
    getDictationControl(paneId) {
        return this._visibleControls.find(
            c => c.paneId === paneId && c.type === ControlType.DICTATION
        ) || null
    }

    /**
     * Prüft ob das Sync-Warn-Control für einen Bereich sichtbar ist.
     * @param {string} paneId
     */
    hasSyncWarningControl(paneId) {
        return this._visibleControls.some(
            c => c.paneId === paneId && c.type === ControlType.SYNC_WARNING
        )
    }

    /**
     * Gibt das Sync-Warn-Control für einen Bereich zurück.
     * @param {string} paneId
     */
    getSyncWarningControl(paneId) {
        return this._visibleControls.find(
            c => c.paneId === paneId && c.type === ControlType.SYNC_WARNING
        ) || null
    }

    /**
     * Prüft ob ein Abort-Control für einen Bereich sichtbar ist.
     * @param {string} paneId
     * @returns {boolean}
     */
    hasAbortControl(paneId) {
        return this._visibleControls.some(
            c => c.paneId === paneId && c.type === ControlType.ABORT
        )
    }

    /**
     * Gibt das Abort-Control für einen Bereich zurück.
     * @param {string} paneId
     * @returns {Control|null}
     */
    getAbortControl(paneId) {
        return this._visibleControls.find(
            c => c.paneId === paneId && c.type === ControlType.ABORT
        ) || null
    }

    /**
     * Prüft ob ein Lock-Control für einen Bereich sichtbar ist.
     * @param {string} paneId
     * @returns {boolean}
     */
    hasLockControl(paneId) {
        return this._visibleControls.some(
            c => c.paneId === paneId && c.type === ControlType.LOCK
        )
    }

    /**
     * Gibt das Lock-Control für einen Bereich zurück.
     * @param {string} paneId
     * @returns {Control|null}
     */
    getLockControl(paneId) {
        return this._visibleControls.find(
            c => c.paneId === paneId && c.type === ControlType.LOCK
        ) || null
    }

    /**
     * Aktiviert die aktuelle Station (Enter).
     * Nur wirksam auf Controls, nicht auf Cursor-Positionen.
     * @returns {boolean} true wenn Aktion ausgeführt wurde
     */
    activateCurrentStation() {
        if (!this.currentStation || !this.currentStation.isControl()) {
            return false
        }

        // Separator-Control: Callback auslösen, kein Layout-Wechsel
        if (this.currentStation.type === StationType.CONTROL_SEPARATOR) {
            if (this._onSeparatorInsert) this._onSeparatorInsert()
            return true
        }

        // Background-Control: Callback auslösen, kein Layout-Wechsel
        if (this.currentStation.type === StationType.CONTROL_BACKGROUND) {
            if (this._onBackgroundInsert) this._onBackgroundInsert()
            return true
        }

        // Dictation-Control: Callback auslösen, kein Layout-Wechsel
        if (this.currentStation.type === StationType.CONTROL_DICTATION) {
            if (this._onDictationToggle) this._onDictationToggle()
            return true
        }

        // Abort-Control: Agent-Loop abbrechen, kein Layout-Wechsel
        if (this.currentStation.type === StationType.CONTROL_ABORT) {
            if (this._onAgentAbort) this._onAgentAbort()
            return true
        }

        // Lock-Control: Callback auslösen, kein Layout-Wechsel
        if (this.currentStation.type === StationType.CONTROL_LOCK) {
            if (this._onLockToggle) this._onLockToggle()
            return true
        }

        const pane = this.panes[this.currentStation.paneId]

        if (this.currentStation.type === StationType.CONTROL_EXPAND) {
            this._handleExpand(pane)
        } else {
            this._handleCollapse(pane)
        }

        // Bounds neu berechnen
        this.calculateBounds()

        // Cursor-Verhalten nach Aktivierung
        const isNowExpanded = pane.isExpanded()

        if (isNowExpanded) {
            // Bereich ist sichtbar -> Cursor rein
            this.activePane = pane.id
            // Zur Cursor-Station dieses Bereichs wechseln
            this.currentStation = this._cursorStations.find(
                s => s.paneId === pane.id
            ) || null
        } else {
            // Bereich ist collapsed -> auf + bleiben
            this.currentStation = this._allStations.find(
                s => s.paneId === pane.id && s.type === StationType.CONTROL_EXPAND
            ) || null
        }

        return true
    }

    // Legacy-Methoden für Abwärtskompatibilität
    get navigationMode() {
        return this.currentStation !== null && this.currentStation.isControl()
    }

    enterNavigationMode() {
        // Finde erstes Control des aktiven Bereichs
        const station = this._allStations.find(
            s => s.paneId === this.activePane && s.isControl()
        )
        this.currentStation = station || this._allStations[0]
        this.isDirty = true
        this.borderDirty = true
    }

    exitNavigationMode() {
        // Zur Cursor-Station des aktiven Bereichs
        this.currentStation = this._cursorStations.find(
            s => s.paneId === this.activePane
        ) || null
        this.isDirty = true
        this.borderDirty = true
    }

    getCurrentControl() {
        if (!this.currentStation || !this.currentStation.isControl()) {
            return null
        }
        // Konvertiere zu Control für Rendering
        const ctrlType = this.currentStation.type === StationType.CONTROL_EXPAND
            ? ControlType.EXPAND
            : this.currentStation.type === StationType.CONTROL_SEPARATOR
            ? ControlType.SEPARATOR
            : this.currentStation.type === StationType.CONTROL_BACKGROUND
            ? ControlType.BACKGROUND
            : this.currentStation.type === StationType.CONTROL_DICTATION
            ? ControlType.DICTATION
            : this.currentStation.type === StationType.CONTROL_ABORT
            ? ControlType.ABORT
            : this.currentStation.type === StationType.CONTROL_LOCK
            ? ControlType.LOCK
            : ControlType.COLLAPSE
        return new Control(
            this.currentStation.paneId,
            ctrlType,
            this.currentStation.x,
            this.currentStation.y
        )
    }

    activateCurrentControl() {
        return this.activateCurrentStation()
    }

    /**
     * Behandelt + Klick (Expand/Fullscreen).
     * @param {Pane} pane
     */
    _handleExpand(pane) {
        if (pane.state === PaneState.COLLAPSED) {
            // Collapsed -> Normal
            pane.state = PaneState.NORMAL
        } else if (pane.state === PaneState.NORMAL) {
            // Normal -> Fullscreen
            pane.previousState = PaneState.NORMAL
            pane.state = PaneState.FULLSCREEN
        }
        // Fullscreen + nochmal + -> nichts
    }

    /**
     * Klappt einen eingeklappten Bereich auf, OHNE Fokus/Cursor dorthin zu
     * ziehen — fuer System-Anlaesse wie das Auto-Aufklappen der Console bei
     * ERROR-Zeilen (2026-07-02). Der Schreibfluss im aktiven Pane bleibt
     * unangetastet; expandierte Panes werden nicht angefasst.
     * @param {string} paneId
     */
    expandPaneQuietly(paneId) {
        const pane = this.panes[paneId]
        if (!pane || pane.state !== PaneState.COLLAPSED) return
        this._handleExpand(pane)
        this.calculateBounds()
        this.isDirty = true
        this.borderDirty = true
        if (this._onPaneStateChanged) this._onPaneStateChanged()
    }

    /**
     * Behandelt - Klick (Collapse/Exit Fullscreen).
     * @param {Pane} pane
     */
    _handleCollapse(pane) {
        if (pane.state === PaneState.FULLSCREEN) {
            // Fullscreen -> vorheriger Zustand
            pane.state = pane.previousState || PaneState.NORMAL
            pane.previousState = null
        } else if (pane.state === PaneState.NORMAL && pane.canCollapse) {
            // Normal -> Collapsed
            pane.state = PaneState.COLLAPSED
        }
        // Collapsed - -> nichts (Button sollte nicht sichtbar sein)
    }

    /**
     * Findet den Bereich an einer bestimmten Position.
     * @param {number} x - Spalte
     * @param {number} y - Zeile
     * @returns {Pane|null}
     */
    getPaneAtPosition(x, y) {
        for (const pane of Object.values(this.panes)) {
            if (!pane.isVisible()) continue

            const b = pane.bounds
            if (x >= b.x && x < b.x + b.width && y >= b.y && y < b.y + b.height) {
                return pane
            }
        }
        return null
    }

    /**
     * Findet ein Control an einer bestimmten Position.
     * @param {number} x - Spalte
     * @param {number} y - Zeile
     * @param {{dx:number,dy:number}} [slop] - Toleranz fuer Touch-Hit-Tests.
     *   Default {0,0} = pixelgenau (Maus). Bei Slop > 0 werden Controls in der
     *   erweiterten Box geprueft; bei mehreren Treffern gewinnt der naechste
     *   (euklidische Distanz in Char-Einheiten).
     * @returns {Control|null}
     */
    getControlAtPosition(x, y, slop = null) {
        const dx = slop ? slop.dx : 0
        const dy = slop ? slop.dy : 0

        // Schnellpfad ohne Slop: bestehende Logik 1:1 (Maus-Hit-Test bleibt unveraendert)
        if (dx === 0 && dy === 0) {
            for (const control of this._visibleControls) {
                if (control.y !== y) continue
                if (this._isProportional) {
                    if (x >= control.x - 1 && x <= control.x + 1) return control
                } else {
                    if (x === control.x) return control
                }
            }
            return null
        }

        // Touch-Pfad mit Slop: alle Treffer in der erweiterten Box sammeln,
        // den naechsten zurueckgeben.
        let best = null
        let bestDistSq = Infinity
        const propRadius = this._isProportional ? 1 : 0
        for (const control of this._visibleControls) {
            if (Math.abs(control.y - y) > dy) continue
            // Im Proportional-Mode liegt die Hit-Box bei [x-1, x+1]; Slop addiert beidseits dx.
            const minX = control.x - propRadius - dx
            const maxX = control.x + propRadius + dx
            if (x < minX || x > maxX) continue
            const distSq = (control.x - x) * (control.x - x) + (control.y - y) * (control.y - y)
            if (distSq < bestDistSq) {
                bestDistSq = distSq
                best = control
            }
        }
        return best
    }

    /**
     * Setzt den Hover-State für ein Control.
     * @param {Control|null} control
     */
    setHoveredControl(control) {
        if (this.hoveredControl !== control) {
            this.hoveredControl = control
            this.isDirty = true
            this.borderDirty = true
        }
    }

    /**
     * Behandelt Maus-Klick auf ein Control.
     * @param {Control} control
     */
    handleControlClick(control) {
        // Separator-Control: Callback auslösen
        if (control.type === ControlType.SEPARATOR) {
            if (this._onSeparatorInsert) this._onSeparatorInsert()
            return
        }

        // Background-Control: Callback auslösen
        if (control.type === ControlType.BACKGROUND) {
            if (this._onBackgroundInsert) this._onBackgroundInsert()
            return
        }

        // Dictation-Control: Callback auslösen
        if (control.type === ControlType.DICTATION) {
            if (this._onDictationToggle) this._onDictationToggle()
            return
        }

        // Abort-Control: Agent-Loop abbrechen
        if (control.type === ControlType.ABORT) {
            if (this._onAgentAbort) this._onAgentAbort()
            return
        }

        // Lock-Control: Callback auslösen
        if (control.type === ControlType.LOCK) {
            if (this._onLockToggle) this._onLockToggle()
            return
        }

        // Sync-Warn-Control: Erklaer-Dialog, kein Layout-Wechsel
        if (control.type === ControlType.SYNC_WARNING) {
            if (this._onSyncWarningClick) this._onSyncWarningClick()
            return
        }

        const pane = this.panes[control.paneId]
        const wasActive = this.activePane === pane.id
        const wasExpanded = pane.isExpanded()

        if (control.type === ControlType.EXPAND) {
            this._handleExpand(pane)
        } else {
            this._handleCollapse(pane)
        }

        this.calculateBounds()

        const isNowExpanded = pane.isExpanded()

        // Wenn aktiver Bereich eingeklappt wurde: nächsten sichtbaren aktivieren
        if (wasActive && !isNowExpanded) {
            this._activateNextVisiblePane(pane.id)
            // Cursor auf den neuen aktiven Bereich setzen
            this.currentStation = this._cursorStations.find(
                s => s.paneId === this.activePane
            ) || null
        }
        // Wenn Bereich ausgeklappt wurde: aktivieren und Cursor setzen
        else if (isNowExpanded) {
            this.activePane = pane.id
            // Cursor auf den neu aktiven Bereich setzen
            this.currentStation = this._cursorStations.find(
                s => s.paneId === pane.id
            ) || null
        }

        // Hook: Cursor-State (insb. Separator-Edit-Pane) ans neue Layout anpassen
        if (this._onPaneStateChanged) this._onPaneStateChanged()
    }

    /**
     * Aktiviert den nächsten sichtbaren Bereich in Tab-Reihenfolge.
     * @param {string} excludePaneId - Bereich der übersprungen werden soll
     */
    _activateNextVisiblePane(excludePaneId) {
        const order = [PaneId.OUTLINE, PaneId.MAIN, PaneId.CONTEXT, PaneId.CORE]

        // Starte bei dem Bereich nach dem ausgeschlossenen
        const startIdx = order.indexOf(excludePaneId)
        for (let i = 1; i <= order.length; i++) {
            const idx = (startIdx + i) % order.length
            const pane = this.panes[order[idx]]
            if (pane.isExpanded()) {
                this.activePane = pane.id
                return
            }
        }

        // Fallback: Main ist immer da
        this.activePane = PaneId.MAIN
    }

    /**
     * Ändert die Dimensionen (nach Window-Resize).
     * @param {number} cols
     * @param {number} rows
     */
    resize(cols, rows) {
        this.cols = cols
        this.rows = rows
        this.calculateBounds()
    }

    /**
     * Gibt den aktuellen Zustand für Persistenz zurück.
     * @returns {Object}
     */
    getState() {
        // MEMORY und PERSONAL_DICT sind transient — wenn der User gerade im
        // entsprechenden Editor ist, speichern wir MAIN als Fallback. Sonst
        // kaeme nach Reload activePane=MEMORY/PERSONAL_DICT ohne offenen
        // Editor heraus.
        const transient = this._activeEditorPane === PaneId.MEMORY
            || this._activeEditorPane === PaneId.PERSONAL_DICT
        const activeForPersist = transient ? PaneId.MAIN : this._activeEditorPane
        return {
            outline: this.panes[PaneId.OUTLINE].state,
            main: this.panes[PaneId.MAIN].state,
            context: this.panes[PaneId.CONTEXT].state,
            core: this.panes[PaneId.CORE].state,
            // Immer den Editor-Pane speichern (Console-Fokus ist transient)
            activePane: activeForPersist
        }
    }

    /**
     * Stellt den Zustand aus Persistenz wieder her.
     * @param {Object} state
     */
    setState(state) {
        if (!state) return

        if (state.outline) {
            this.panes[PaneId.OUTLINE].state = state.outline
        }
        if (state.main) {
            this.panes[PaneId.MAIN].state = state.main
        }
        if (state.context) {
            this.panes[PaneId.CONTEXT].state = state.context
        }
        if (state.core) {
            this.panes[PaneId.CORE].state = state.core
        }
        if (state.activePane && this.panes[state.activePane]) {
            // CORE, MEMORY oder PERSONAL_DICT aus Persistenz-Daten ignorieren —
            // alle drei duerfen nicht der "stabile" aktive Editor-Pane sein.
            if (state.activePane === PaneId.CORE
                || state.activePane === PaneId.MEMORY
                || state.activePane === PaneId.PERSONAL_DICT
            ) {
                this._activeEditorPane = PaneId.MAIN
            } else {
                this._activeEditorPane = state.activePane
            }
        }
        this.consoleFocus = ConsoleFocus.NONE

        this.calculateBounds()
    }

    /**
     * Zeigt die Tab-Leiste an (eine Zeile oben).
     */
    showTabBar() {
        if (this.tabBarVisible) return
        this.tabBarVisible = true
        this.calculateBounds()
        this.borderDirty = true
    }

    /**
     * Versteckt die Tab-Leiste.
     */
    hideTabBar() {
        if (!this.tabBarVisible) return
        this.tabBarVisible = false
        this.calculateBounds()
        this.borderDirty = true
    }

    /**
     * Markiert als sauber (nach Rendering).
     */
    markClean() {
        this.isDirty = false
    }
}
